<script setup lang="ts">
import DynamicsSvg from '@/assets/svg/dynamics.svg'
import { useDateFormatter } from '@/helpers/DateFormatter'
import { buildDynamicsUriPathForWo } from '@/helpers/WorkOrderHelper'
import type { IWorkOrder } from '@/models/WorkOrder/IWorkOrder'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ workOrder: IWorkOrder | null | undefined }>()

const { t, locale } = useI18n()
const { formatDateByLocale } = useDateFormatter()

const getDynamicsUrlForWo = () => {
  let url = ''
  if (props.workOrder?.WorkOrderId) {
    url = `${import.meta.env.VITE_DYNAMICS_URL}` + buildDynamicsUriPathForWo(props.workOrder?.WorkOrderId)
  }
  return url
}
</script>

<template>
  <div class="flex-1 min-w-[300px] max-w-full">
    <div class="text-2xl mb-2 font-semibold flex items-center gap-2" data-cy="workorderdetails-number">
      {{ t('workOrderDetails.workOrder') + ' - ' + workOrder?.WorkOrderNumber }}
      <div v-if="workOrder">
        <a v-if="workOrder.WorkOrderId" :href="getDynamicsUrlForWo()" class="dynamics-link" target="_blank">
          <Image v-if="workOrder.WorkOrderId" width="16" :src="DynamicsSvg" class="img-width" v-tooltip.bottom="{ value: t('caseManagement.details.openInDynamics') }" data-cy="b2b-dynamics-button" />
        </a>
      </div>
    </div>
  </div>
  <div class="p-4">
    <div class="flex-1 min-w-[300px] max-w-full">
      <div class="text-lg mb-2 underline font-semibold" data-cy="workorderdetails-information-title">{{ t('workOrderDetails.information.title') }}</div>
    </div>
    <div class="flex flex-wrap gap-4 align-items-stretch">
      <div class="flex-1 min-w-[300px] max-w-full md:basis-1/2">
        <div class="flex flex-column mb-6">
          <div class="!grid grid-cols-[auto_1fr] min-w-[20rem] items-start">
            <div class="m-2 min-w-[10rem]" data-cy="workorderdetails-status-label">{{ t('workOrderDetails.information.status') }}</div>
            <div class="col font-semibold" data-cy="workorderdetails-status">{{ workOrder?.Status ?? '-' }}</div>
          </div>
          <div class="!grid grid-cols-[auto_1fr] min-w-[20rem] items-start">
            <div class="m-2 min-w-[10rem]" data-cy="workorderdetails-type-label">{{ t('workOrderDetails.information.type') }}</div>
            <div class="col font-semibold" data-cy="workorderdetails-type">{{ workOrder?.WorkOrderType ?? '-' }}</div>
          </div>
          <div class="!grid grid-cols-[auto_1fr] min-w-[20rem] items-start">
            <div class="m-2 min-w-[10rem]" data-cy="workorderdetails-sla-label">{{ t('workOrderDetails.information.sla') }}</div>
            <div class="col font-semibold" data-cy="workorderdetails-sla">{{ workOrder?.ServiceLevelAgreement ?? '-' }}</div>
          </div>
          <div class="!grid grid-cols-[auto_1fr] min-w-[20rem] items-start">
            <div class="m-2 min-w-[10rem]" data-cy="workorderdetails-serviceparty-label">{{ t('workOrderDetails.information.serviceParty') }}</div>
            <div class="col font-semibold" data-cy="workorderdetails-serviceparty">{{ workOrder?.ServiceParty ?? '-' }}</div>
          </div>
          <div class="!grid grid-cols-[auto_1fr] min-w-[20rem] items-start">
            <div class="m-2 min-w-[10rem]" data-cy="workorderdetails-chargepoint-label">{{ t('workOrderDetails.information.chargePoint') }}</div>
            <div class="col font-semibold" data-cy="workorderdetails-chargepoint">
              <div v-if="workOrder?.ChargePointNumber">
                <router-link :to="{ path: '/chargepointdetails/' + workOrder.ChargePointNumber }" class="cp-title-hyperlink font-semibold">
                  {{ workOrder.ChargePointNumber }}
                </router-link>
              </div>
              <div v-else>-</div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex-1 min-w-[300px] max-w-full md:basis-1/2">
        <div class="!grid grid-cols-[auto_1fr] min-w-[20rem] items-start">
          <div class="m-2 min-w-[10rem]" data-cy="workorderdetails-owner-label">{{ t('workOrderDetails.information.owner') }}</div>
          <div class="col font-semibold" data-cy="workorderdetails-owner">{{ workOrder?.Owner ?? '-' }}</div>
        </div>
        <div class="!grid grid-cols-[auto_1fr] min-w-[20rem] items-start">
          <div class="m-2 min-w-[10rem]" data-cy="workorderdetails-createdon-label">{{ t('workOrderDetails.information.createdOn') }}</div>
          <div class="col font-semibold" data-cy="workorderdetails-createdon">{{ workOrder?.CreatedOn ? formatDateByLocale(workOrder.CreatedOn.toString(), locale) : '-' }}</div>
        </div>
        <div class="!grid grid-cols-[auto_1fr] min-w-[20rem] items-start">
          <div class="m-2 min-w-[10rem]" data-cy="workorderdetails-createdby-label">{{ t('workOrderDetails.information.createdBy') }}</div>
          <div class="col font-semibold" data-cy="workorderdetails-createdby">{{ workOrder?.CreatedBy ?? '-' }}</div>
        </div>
        <div class="!grid grid-cols-[auto_1fr] min-w-[20rem] items-start">
          <div class="m-2 min-w-[10rem]" data-cy="workorderdetails-planneddate-label">{{ t('workOrderDetails.information.plannedDate') }}</div>
          <div class="col font-semibold" data-cy="workorderdetails-planneddate">{{ workOrder?.PlannedDate ? formatDateByLocale(workOrder.PlannedDate.toString(), locale) : '-' }}</div>
        </div>
        <div class="!grid grid-cols-[auto_1fr] min-w-[20rem] items-start">
          <div class="m-2 min-w-[10rem]" data-cy="workorderdetails-caseid-label">{{ t('workOrderDetails.information.caseNumber') }}</div>
          <div class="col font-semibold" data-cy="workorderdetails-caseid">
            <div v-if="workOrder?.CaseNumber">
              <router-link :to="{ path: '/casemanagement/' + workOrder.CaseNumber }" class="cp-title-hyperlink font-semibold">
                {{ workOrder.CaseNumber }}
              </router-link>
            </div>
            <div v-else>-</div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex-1 min-w-[300px] max-w-full">
      <div class="text-lg mb-2 underline font-semibold" data-cy="workorderdetails-casedescription-title">{{ t('workOrderDetails.information.caseDescription') }}</div>
    </div>
    <div class="col font-semibold" data-cy="workorderdetails-casedescription">{{ workOrder?.CaseDescription ?? '-' }}</div>
    <div class="flex-1 min-w-[300px] max-w-full mt-4">
      <div class="text-lg mb-2 underline font-semibold" data-cy="workorderdetails-caseremarks-title">{{ t('workOrderDetails.information.caseRemarks') }}</div>
    </div>
    <div class="col font-semibold" data-cy="workorderdetails-caseremarks">{{ workOrder?.CaseRemarks ?? '-' }}</div>
  </div>
</template>

<style scoped>
.img-width {
  width: 0.8rem;
  cursor: pointer;
}

.dynamics-link {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  text-decoration: none;
}
</style>
